import { Capacitor } from '@capacitor/core';
import apiClient from 'services/api';
import { ehNativo, plataforma } from 'services/plataforma';
import { lerAparelhoPush, limparAparelhoPush, salvarAparelhoPush, temSessao } from 'services/sessao';

/**
 * Notificação push no app — o lado do aparelho. O raciocínio inteiro está em
 * prompts/notificacoes-push.md; o resumo que este arquivo precisa:
 *
 * - Todo aviso do sino também sai por push. Para quem tem o app com notificação ativa, o push
 *   SUBSTITUI o SMS: o backend só manda SMS quando nenhum aparelho aceita (AvisoExternoService).
 * - Por isso o token só vai ao backend com a permissão CONCEDIDA. Com ela negada o Firebase aceita o
 *   envio e o Android o descarta em silêncio — o backend acharia que avisou, e o SMS não sairia.
 * - Pelo mesmo motivo, a cada sincronização a permissão é relida: tirada nas configurações do
 *   Android, o token sai do backend e o SMS volta a valer.
 *
 * Na web tudo aqui é no-op: o plugin só é importado atrás de `ehNativo()`, como os outros
 * (hooks/useOnline.js), e nem entra no chunk do site.
 */

/** O canal de notificação do Android. O backend manda o mesmo id (PushService.CANAL_ANDROID). */
export const CANAL_AVISOS = 'avisos';

/** Disparado em `window` quando chega um push com o app aberto — o NavBar recontar o sino. */
export const EVENTO_PUSH_RECEBIDO = 'novafaxina:push-recebido';

/**
 * De quanto em quanto tempo o app reconfirma o mesmo token ao backend. A reconfirmação é o que mantém
 * o aparelho "ativo" lá (janela de 30 dias, push.dias-aparelho-ativo); fazer isso a cada volta ao app
 * seria uma requisição por desbloqueio de tela, sem ganho nenhum.
 */
const INTERVALO_RECONFIRMACAO_MS = 12 * 60 * 60 * 1000;

/**
 * Se dá para usar push aqui: app nativo E plugin presente na ponte.
 *
 * `ehNativo()` sozinho não basta. Sem implementação, o `addListener` do Capacitor deixa uma promessa
 * rejeitada solta dentro dele — que nenhum try/catch daqui alcança. Perguntar antes evita tocar no
 * plugin onde ele não existe (um build sem o `cap sync`, ou os testes que simulam o app).
 */
export function pushDisponivel() {
	return ehNativo() && Capacitor.isPluginAvailable('PushNotifications');
}

/**
 * Devolve o MÓDULO, nunca o plugin, e quem chama desestrutura: `const { PushNotifications } = await
 * carregarPlugin()`.
 *
 * O objeto do plugin é um Proxy do Capacitor que inventa um método para qualquer nome, inclusive
 * `then`. Devolvê-lo de uma função async faz o JavaScript tratá-lo como promessa e chamar
 * `PushNotifications.then()`, que vai à ponte nativa e falha com "not implemented on android". Foi o
 * que aconteceu na primeira build: toda sincronização morria aqui, antes de pedir a permissão.
 */
function carregarPlugin() {
	return import('@capacitor/push-notifications');
}

/**
 * Envia o token ao backend quando ele é novo ou a última confirmação já envelheceu.
 * Exportado para o teste; quem chama de verdade é o listener de `registration`.
 */
export async function confirmarToken(token) {
	if (!token || !temSessao()) return;

	const guardado = lerAparelhoPush();
	const recente = guardado?.token === token
		&& Date.now() - (guardado.confirmadoEm || 0) < INTERVALO_RECONFIRMACAO_MS;
	if (recente) return;

	try {
		await apiClient.put('/push/dispositivo', { token, plataforma: plataforma() });
		salvarAparelhoPush(token);
	} catch {
		// Sem rede ou backend fora: tenta de novo na próxima sincronização. Enquanto isso o aparelho
		// continua (ou não) ativo pelo que o backend já tinha, e o SMS cobre o resto.
	}
}

/**
 * Os ouvintes do plugin, uma vez por montagem do app.
 *
 * Instalados logo no início, com ou sem sessão, por causa do toque: quando o app é ABERTO pelo toque
 * numa notificação, o plugin guarda o `pushNotificationActionPerformed` até alguém ouvir — mas só
 * entrega se o ouvinte existir. Esperar o login para instalar perderia exatamente esse toque.
 *
 * @param {{ aoTocar: (dados: object) => void }} opcoes
 * @returns {Promise<() => void>} remove os ouvintes
 */
export async function instalarOuvintesPush({ aoTocar }) {
	if (!pushDisponivel()) return () => {};

	const { PushNotifications } = await carregarPlugin();
	const inscricoes = await Promise.all([
		PushNotifications.addListener('registration', ({ value }) => confirmarToken(value)),
		PushNotifications.addListener('registrationError', () => {
			// Sem Google Play Services, ou google-services.json ausente no build. Nada a fazer: os
			// avisos seguem por SMS, que é o que o backend faz quando não há aparelho.
		}),
		PushNotifications.addListener('pushNotificationReceived', () => {
			// App aberto: o Android NÃO mostra a notificação na bandeja. O sino é o aviso.
			window.dispatchEvent(new CustomEvent(EVENTO_PUSH_RECEBIDO));
		}),
		PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
			aoTocar(notification?.data || {});
		}),
	]);

	return () => inscricoes.forEach((inscricao) => inscricao.remove());
}

/**
 * A notificação tocada já foi vista: sai da contagem do sino. Sem sessão não há como (o toque abriu
 * o app deslogado e a guarda leva ao login); falha também não importa — abrir o sino marca todas.
 * O evento faz o NavBar recontar, como na chegada de um push com o app aberto.
 */
export async function marcarNotificacaoTocada(notificacaoId) {
	if (!notificacaoId || !temSessao()) return;
	try {
		await apiClient.put(`/notificacao/read/${encodeURIComponent(notificacaoId)}`);
		window.dispatchEvent(new CustomEvent(EVENTO_PUSH_RECEBIDO));
	} catch {
		// Fica não lida até a pessoa abrir o sino.
	}
}

/**
 * Tira o token deste aparelho do backend e do Firebase. Usado quando a permissão foi tirada.
 */
async function esquecerAparelho(PushNotifications) {
	const guardado = lerAparelhoPush();
	if (guardado?.token && temSessao()) {
		try {
			await apiClient.delete('/push/dispositivo', { data: { token: guardado.token } });
		} catch {
			// Se falhar, o backend ainda tenta o push; o FCM aceita e o Android descarta. É o risco que
			// existe até a próxima sincronização — e por isso o `unregister` abaixo: com o token
			// invalidado no Firebase, o envio volta como UNREGISTERED e o backend cai para o SMS.
		}
	}
	limparAparelhoPush();
	try {
		await PushNotifications.unregister();
	} catch {
		// Já não estava registrado.
	}
}

let sincronizacaoEmCurso = null;

/**
 * Deixa o backend com a verdade sobre este aparelho: registrado se a permissão está concedida, fora
 * se não está.
 *
 * @param {{ podePerguntar?: boolean }} opcoes `podePerguntar` só no painel, depois do login: o pedido
 *   de permissão nunca aparece na instalação nem no meio de um cadastro. Sem ele, a sincronização só
 *   confirma ou retira o que já foi decidido.
 */
export function sincronizarPush({ podePerguntar = false } = {}) {
	if (!pushDisponivel() || !temSessao()) return Promise.resolve();
	// A volta ao app e a chegada ao painel podem disparar juntas; uma rodada basta.
	if (sincronizacaoEmCurso) return sincronizacaoEmCurso;

	sincronizacaoEmCurso = (async () => {
		try {
			const { PushNotifications } = await carregarPlugin();

			// Idempotente. É o nome que a pessoa vê em Configurações > Apps > Nova Faxina > Notificações.
			try {
				await PushNotifications.createChannel({
					id: CANAL_AVISOS,
					name: 'Avisos da Nova Faxina',
					description: 'Agendamentos, confirmações, cancelamentos e outros avisos da sua conta',
					importance: 4,
					visibility: 1,
				});
			} catch {
				// Sem o canal, o Firebase usa o dele ("Diversos"): a notificação chega igual.
			}

			let { receive } = await PushNotifications.checkPermissions();

			// Só o "prompt" puro. O "prompt-with-rationale" é o Android dizendo que a pessoa já negou
			// uma vez: perguntar de novo sozinho seria insistir, e a resposta já foi dada.
			if (receive === 'prompt' && podePerguntar) {
				({ receive } = await PushNotifications.requestPermissions());
			}

			if (receive === 'granted') {
				// O token chega pelo listener de `registration` (instalarOuvintesPush).
				await PushNotifications.register();
			} else if (receive === 'denied' || receive === 'prompt-with-rationale') {
				await esquecerAparelho(PushNotifications);
			}
		} catch {
			// Plugin indisponível: o app segue, e os avisos, por SMS.
		} finally {
			sincronizacaoEmCurso = null;
		}
	})();
	return sincronizacaoEmCurso;
}

/**
 * Logout. Não chama o backend: o /login/logout já apaga os aparelhos junto com as sessões, e a esta
 * altura o JWT já foi apagado (ver services/autenticacao.js). Aqui sai o que é do aparelho: o token
 * no Firebase — o que também cobre o logout sem rede, porque o próximo envio volta UNREGISTERED — e
 * as notificações já entregues, que num celular compartilhado mostrariam avisos da conta que saiu.
 */
export async function encerrarPush() {
	if (!pushDisponivel()) return;
	limparAparelhoPush();
	try {
		const { PushNotifications } = await carregarPlugin();
		try {
			await PushNotifications.removeAllDeliveredNotifications();
		} catch {
			// Não impede o unregister abaixo, que é o que importa.
		}
		await PushNotifications.unregister();
	} catch {
		// Já não estava registrado, ou o plugin não está disponível.
	}
}
